/**
 * Entitlements service — fetches active service entitlements for the
 * authenticated customer's tenant from the backend API.
 *
 * Falls back to hardcoded mock data when the API is unavailable
 * (e.g. local dev without the Azure Functions backend running).
 */

import { fetchEntitlements as fetchEntitlementsApi } from "../utils/api";
import { isMsalConfigured } from "../auth/msalConfig";

const MOCK_ENTITLEMENTS = {
  customer_name: "Contoso Bank",
  customer_id: "PC-9999",
  tenant_id: "00000000-0000-0000-0000-000000000000",
  slug: "contoso-bank",
  industry: "Banking",
  account_executive: null,
  active_services: [
    {
      slug: "secureshield",
      service_name: "SecureShield 365",
      tier: "Gold",
      status: "active",
      start_date: "2026-03-04T00:00:00Z",
      expiration_date: "2027-03-03T00:00:00Z",
      auto_renew: true,
    },
    {
      slug: "academy",
      service_name: "Patriot Academy",
      tier: null,
      status: "active",
      url: "https://premium.patriotconsulting.com/",
    },
  ],
};

/**
 * Fetch entitlements for a given Entra tenant ID.
 * @param {string} tenantId — Azure AD tenant GUID
 * @returns {Promise<Object>} — entitlements payload
 */
export async function fetchEntitlements(tenantId) {
  try {
    return await fetchEntitlementsApi(tenantId);
  } catch (err) {
    // Mock fallback is ONLY for local dev with no MSAL configured. In any
    // real (MSAL-configured) environment we must NOT silently substitute
    // mock data — doing so previously masked production 403s and stamped the
    // user with the mock "contoso-bank" slug. Re-throw so the caller knows
    // entitlements genuinely failed and can show an "unavailable" state.
    if (!isMsalConfigured) {
      console.warn(
        `[entitlements] Local-dev fallback to MOCK data for tenant "${tenantId}". ` +
        `API request failed: ${err.message}. ` +
        `Check that the backend is running and that this tenant is registered in the customers table.`
      );
      return MOCK_ENTITLEMENTS;
    }
    throw err;
  }
}
